import {
  getCompatibleChartsForMetric,
  isSavedQueryVariableMetric,
  METRIC_DATA_TYPE_REGISTRY,
} from '../data-type';
import type { SelectableChartType } from './types/chart';
import type { EntityType, SecondaryDimensionValues } from './types/dimension';
import type { MetricValues } from './types/metric';

/**
 * @deprecated Prefer `getCompatibleChartsForMetric` / the DataType registry.
 * Retained for callers that import the raw map; values are derived from
 * `METRIC_DATA_TYPE_REGISTRY` and `CHART_INPUT_REQUIREMENTS` intersection logic.
 */
const ALLOWED_CHART_TYPES_BY_METRIC_BASE: Partial<
  Record<MetricValues, SelectableChartType[]>
> = (() => {
  const derived: Partial<Record<MetricValues, SelectableChartType[]>> = {};
  (Object.keys(METRIC_DATA_TYPE_REGISTRY) as MetricValues[]).forEach(
    (metric) => {
      const charts = getCompatibleChartsForMetric(metric);
      if (charts.length > 0) {
        derived[metric] = charts;
      }
    }
  );
  return derived;
})();

const COMPANY_GROUPED_STACK_DIVERGING_COMBO_METRICS: Partial<
  Record<MetricValues, boolean>
> = {
  headcount: true,
  base_salary: true,
  contractor_pay_rate: true,
  total_compensation: true,
  total_spend_salary: true,
  total_spend_compensation: true,
  transitions_inflow: true,
  transitions_outflow: true,
  overall: true,
  posting_salary: true,
  postings_active: true,
  postings_new: true,
  postings_removed: true,
  screener_headcount: true,
  sentiment_top_bottom: true,
  time_to_fill: true,
};

const COMPANY_STACKED_BAR_COMBO_METRICS: Partial<
  Record<MetricValues, boolean>
> = {
  headcount: true,
  total_spend_salary: true,
  total_spend_compensation: true,
  transitions_inflow: true,
  transitions_outflow: true,
};

// Entity type constraints - which metrics/chart types are available per entity type
const ENTITY_TYPE_CONSTRAINTS: Record<
  EntityType,
  {
    allowedMetrics?: MetricValues[];
    chartTypeOverrides?: Partial<
      Record<SelectableChartType, Partial<Record<MetricValues, boolean>>>
    >;
  }
> = {
  company: {
    chartTypeOverrides: {
      treemap: {
        headcount: true,
        total_spend_salary: true,
        total_spend_compensation: true,
      },
      'grouped-bar': COMPANY_GROUPED_STACK_DIVERGING_COMBO_METRICS,
      'stacked-bar': COMPANY_STACKED_BAR_COMBO_METRICS,
      'diverging-bar-combo': COMPANY_GROUPED_STACK_DIVERGING_COMBO_METRICS,
    },
  },
  geography: {
    chartTypeOverrides: {
      treemap: {
        headcount: true,
        total_spend_salary: true,
        total_spend_compensation: true,
        screener_headcount: true,
        screener_growth: true,
        screener_growth_yoy: true,
        screener_hiring: true,
        screener_attrition: true,
        screener_salary: true,
        screener_tenure: true,
        screener_inflow: true,
        screener_outflow: true,
      },
      'grouped-bar': {
        headcount: true,
        total_compensation: true,
        base_salary: true,
        contractor_pay_rate: true,
        total_spend_salary: true,
        total_spend_compensation: true,
        transitions_inflow: true,
        transitions_outflow: true,
        postings_active: true,
      },
      'stacked-bar': {
        headcount: true,
        contractor_pay_rate: true,
        total_spend_salary: true,
        total_spend_compensation: true,
        transitions_inflow: true,
        transitions_outflow: true,
      },
      'diverging-bar-combo': {
        headcount: true,
        total_compensation: true,
        base_salary: true,
        contractor_pay_rate: true,
        total_spend_salary: true,
        total_spend_compensation: true,
        transitions_inflow: true,
        transitions_outflow: true,
        overall: true,
        sentiment_top_bottom: true,
        screener_headcount: true,
        posting_salary: true,
        postings_active: true,
        postings_new: true,
        postings_removed: true,
        time_to_fill: true,
      },
    },
  },
  role: {
    chartTypeOverrides: {
      treemap: {
        headcount: true,
        total_spend_salary: true,
        total_spend_compensation: true,
      },
      'grouped-bar': {
        headcount: true,
        total_spend_salary: true,
        total_spend_compensation: true,
        transitions_inflow: true,
        transitions_outflow: true,
        postings_active: true,
      },
      'stacked-bar': {
        headcount: true,
        total_spend_salary: true,
        total_spend_compensation: true,
        transitions_inflow: true,
        transitions_outflow: true,
      },
      'diverging-bar-combo': {
        headcount: true,
        base_salary: true,
        contractor_pay_rate: true,
        total_compensation: true,
        total_spend_salary: true,
        total_spend_compensation: true,
        transitions_inflow: true,
        transitions_outflow: true,
        overall: true,
        sentiment_top_bottom: true,
        screener_headcount: true,
        posting_salary: true,
        postings_active: true,
        postings_new: true,
        postings_removed: true,
        time_to_fill: true,
      },
    },
  },
};

// Chart type segment mapping - which dimensions each chart type supports
const CHART_TYPE_TO_SEGMENT_MAPPING: Record<
  SelectableChartType | 'share-of-segment',
  SecondaryDimensionValues[]
> = {
  table: [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'timeseries',
  ],
  'stacked-bar': [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  'grouped-bar': [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  'diverging-bar': [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  'diverging-bar-combo': [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  box: ['company', 'geography', 'role'],
  bar: [
    'rics',
    'company',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'competitors',
  ],
  treemap: [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  line: [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  'share-of-segment': [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  sankey: [],
  volume: [],
  distribution: [], // No secondary dimensions
  map: ['geography'],
  text: [],
  gauge: [],
  'ai-summary': [],
};

const SENTIMENT_SEGMENTS: SecondaryDimensionValues[] = [
  'company',
  'geography',
  'role',
  'skills',
  'seniority',
  'education',
  'ethnicity',
  'gender',
  'timeseries',
  'competitors',
];

// Metric segment mapping - which dimensions each metric supports
const METRIC_TO_SEGMENT_MAPPING: Partial<
  Record<MetricValues, SecondaryDimensionValues[]>
> = {
  headcount: [
    'rics',
    'company',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'timeseries',
    'competitors',
  ],
  growth_rate: ['company', 'geography', 'timeseries', 'competitors'],
  hiring_rate: ['company', 'geography', 'timeseries', 'competitors'],
  attrition_rate: ['company', 'geography', 'timeseries', 'competitors'],
  tenure: ['company', 'geography', 'timeseries', 'competitors'],
  salary: ['company', 'geography', 'timeseries', 'competitors'],

  transitions_inflow: [
    'rics',
    'company',
    'geography',
    'role',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  transitions_outflow: [
    'rics',
    'company',
    'geography',
    'role',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],

  posting_salary: ['company', 'role', 'timeseries'],
  time_to_fill: ['company', 'role', 'timeseries'],
  postings_active: ['company', 'role', 'timeseries'],
  postings_new: ['company', 'role', 'timeseries'],
  postings_removed: ['company', 'role', 'timeseries'],
  postings_volume: [],

  base_salary: ['company', 'geography', 'role', 'competitors'],
  total_spend_salary: ['geography', 'role'],
  total_compensation: ['company', 'geography', 'role', 'competitors'],
  total_spend_compensation: ['geography', 'role'],
  contractor_pay_rate: ['company', 'geography', 'role', 'competitors'],
  base_salary_percentiles: ['company', 'geography', 'role', 'competitors'],
  total_compensation_percentiles: [
    'company',
    'geography',
    'role',
    'competitors',
  ],
  contractor_pay_rate_percentiles: [
    'company',
    'geography',
    'role',
    'competitors',
  ],

  // Sentiment metrics
  overall: SENTIMENT_SEGMENTS,
  sentiment_top_bottom: SENTIMENT_SEGMENTS,
  business_outlook: SENTIMENT_SEGMENTS,
  career_growth: SENTIMENT_SEGMENTS,
  compensation: SENTIMENT_SEGMENTS,
  benefits: SENTIMENT_SEGMENTS,
  healthcare: SENTIMENT_SEGMENTS,
  retirement_pension: SENTIMENT_SEGMENTS,
  childcare: SENTIMENT_SEGMENTS,
  education_assistance: SENTIMENT_SEGMENTS,
  wellness_programs: SENTIMENT_SEGMENTS,
  work_life_balance: SENTIMENT_SEGMENTS,
  diversity_and_inclusion: SENTIMENT_SEGMENTS,
  leadership: SENTIMENT_SEGMENTS,
  culture: SENTIMENT_SEGMENTS,
  remote_work: SENTIMENT_SEGMENTS,
  onboarding: SENTIMENT_SEGMENTS,
  mentorship: SENTIMENT_SEGMENTS,
  technology: SENTIMENT_SEGMENTS,
  promotion_opportunities: SENTIMENT_SEGMENTS,
  paid_time_off: SENTIMENT_SEGMENTS,
  transparency: SENTIMENT_SEGMENTS,
  efficiency_and_bureaucracy: SENTIMENT_SEGMENTS,
  product_and_service_quality: SENTIMENT_SEGMENTS,
  prestige: SENTIMENT_SEGMENTS,
  coworkers: SENTIMENT_SEGMENTS,
  mission_and_vision: SENTIMENT_SEGMENTS,
  workplace_facilities: SENTIMENT_SEGMENTS,
  training: SENTIMENT_SEGMENTS,
  clients_and_customers: SENTIMENT_SEGMENTS,
  company_growth: SENTIMENT_SEGMENTS,
  schedule_flexibility: SENTIMENT_SEGMENTS,
  job_security: SENTIMENT_SEGMENTS,
  overtime: SENTIMENT_SEGMENTS,
  family_support: SENTIMENT_SEGMENTS,
  commission: SENTIMENT_SEGMENTS,
  independence: SENTIMENT_SEGMENTS,
  commute: SENTIMENT_SEGMENTS,
  safety: SENTIMENT_SEGMENTS,
  internal_communication: SENTIMENT_SEGMENTS,
  office_politics: SENTIMENT_SEGMENTS,
  harassment: SENTIMENT_SEGMENTS,
  honesty: SENTIMENT_SEGMENTS,
  ethics: SENTIMENT_SEGMENTS,
  commitment: SENTIMENT_SEGMENTS,
  compliance: SENTIMENT_SEGMENTS,
  integrity: SENTIMENT_SEGMENTS,
  positive_review: SENTIMENT_SEGMENTS,
  negative_review: SENTIMENT_SEGMENTS,
};

// Chart types that REQUIRE a secondary dimension
export const REQUIRED_DIMS_BY_CHART_TYPE: Partial<
  Record<SelectableChartType, SecondaryDimensionValues[]>
> = {
  'diverging-bar': [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  'diverging-bar-combo': [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  'grouped-bar': [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'company',
  ],
  'stacked-bar': [
    'rics',
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
  ],
  // box: secondary dimension is optional when we multiple entities
  map: ['geography'],
};

// Helper to get allowed chart types for a metric, considering entity type
export function getAllowedChartTypesForMetric(
  metric: MetricValues,
  entityType?: EntityType
): SelectableChartType[] {
  const baseAllowed = getCompatibleChartsForMetric(metric);

  if (!entityType) {
    return baseAllowed;
  }

  const constraints = ENTITY_TYPE_CONSTRAINTS[entityType];
  if (!constraints?.chartTypeOverrides) {
    return baseAllowed;
  }

  // Filter based on entity type constraints
  // If a chartType has overrides, only include metrics that are explicitly allowed
  // If a chartType has no override, include all metrics from base list
  return baseAllowed.filter((chartType) => {
    const chartTypeOverrides = constraints.chartTypeOverrides?.[chartType];
    if (!chartTypeOverrides) {
      // No override for this chart type - allow if in base list
      return true;
    }
    if (isSavedQueryVariableMetric(metric)) {
      return true;
    }
    // Override exists for this chart type - check if this metric is explicitly allowed
    return chartTypeOverrides[metric] === true;
  });
}

// Helper to get allowed dimensions for a metric+chartType combination
// Computes intersection of chart type and metric segment mappings.
// Segment mapping is orthogonal to DataType; a future iteration may fold
// secondary dimensions into the shape / data type resolver.
export function getAllowedDimsForMetric(
  metric: MetricValues,
  chartType: SelectableChartType
): SecondaryDimensionValues[] {
  const chartDims = CHART_TYPE_TO_SEGMENT_MAPPING[chartType] ?? [];
  if (isSavedQueryVariableMetric(metric)) {
    return chartDims;
  }
  const metricDims = METRIC_TO_SEGMENT_MAPPING[metric] ?? [];

  // Return intersection of chart type and metric dimensions
  return chartDims.filter((dim) => metricDims.includes(dim));
}

// Check if a combination is valid
export function isValidCombination(
  metric: MetricValues,
  chartType: SelectableChartType,
  dim?: SecondaryDimensionValues,
  entityType?: EntityType
): boolean {
  const allowedCharts = getAllowedChartTypesForMetric(metric, entityType);
  if (!allowedCharts.includes(chartType)) {
    return false;
  }

  if (dim) {
    const allowedDims = getAllowedDimsForMetric(metric, chartType);
    return allowedDims.includes(dim);
  }

  // Check if chart type requires a dimension
  const requiredDims = REQUIRED_DIMS_BY_CHART_TYPE[chartType];
  if (requiredDims && requiredDims.length > 0) {
    return false; // Dimension is required but not provided
  }

  return true;
}

// Export the base mapping for reference
export { ALLOWED_CHART_TYPES_BY_METRIC_BASE as ALLOWED_CHART_TYPES_BY_METRIC };
export { CHART_TYPE_TO_SEGMENT_MAPPING };
export { METRIC_TO_SEGMENT_MAPPING };
export { ENTITY_TYPE_CONSTRAINTS };
